import React, { FC } from 'react'

import { useSelector } from 'react-redux'
import { RealEstateType } from '../../../@types/realEstateType'
import { Intent } from '../../../@types/intent'
import { IntegrationType } from '../../../@types/config'
import { ReduxState } from '../../../store/reducers'
import { WizardFlowType } from '../../../@types/wizardFlowType'

interface UserConsentNoteProps {
  realEstateType?: RealEstateType
  intent?: Intent
  isConsultationAccepted?: boolean
  onLinkClick: () => void
  extendedBuyerLead?: boolean
}

export const UserConsentNote: FC<UserConsentNoteProps> = (props) => {
  const {
    config: { integrationType },
    flow: { wizardFlowType },
  } = useSelector((state: ReduxState) => state)

  if (props.realEstateType === RealEstateType.COMMERCIAL && integrationType != IntegrationType.IMMOVERKAUF24) {
    return userConsentNoteCommercialRealEstate(props.onLinkClick)
  }

  if (integrationType === IntegrationType.IMMOVERKAUF24) {
    return userConsentNoteForIV24Integration(props.onLinkClick)
  }

  if (wizardFlowType === WizardFlowType.SOLAR) {
    return userConsentNoteSolarIntent(props.onLinkClick)
  }

  switch (props.intent) {
    case Intent.MAKLER_SALE:
    case Intent.MAYBE_MAKLER_SALE:
      return userConsentNoteIntentSell(props.onLinkClick)
    case Intent.BUY:
      return props.extendedBuyerLead
        ? userConsentNoteIntentExtendedBuy(props.onLinkClick)
        : userConsentNoteIntentBuy(props.onLinkClick)
    case Intent.LET:
      return userConsentNoteIntentRentOut(props.onLinkClick)
    case Intent.MAKLER_LET:
    case Intent.MAYBE_MAKLER_LET:
      return userConsentNoteIntentRentOutWithOrMaybeWithRealtor(props.onLinkClick)
    case Intent.INDEPENDENT_LET:
      return userConsentNoteIntentRentOutWithoutRealtor(props.onLinkClick)
    case Intent.RENT:
      return userConsentNoteIntentRent(props.onLinkClick)
    case Intent.INDEPENDENT_SALE:
      return props.isConsultationAccepted
        ? userConsentNoteIntentIndependentSaleAndConsultation(props.onLinkClick)
        : userConsentNoteIntentIndependentSale(props.onLinkClick)
    case Intent.PARTIAL_SALE:
      return userConsentNoteIntentPartialSale(props.onLinkClick)
    case Intent.INDEPENDENT_WITH_OFFER_SALE:
      return userConsentNoteIntentIndependentWithOfferSale(props.onLinkClick)
    default:
      return <div></div>
  }
}

const userConsentNoteIntentSell = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-sell'>
    Zur Erstellung personalisierter Angebote wird ImmoScout24 deine Daten wie im{' '}
    <span
      className='cursor-pointer'
      data-testid='user-consent-note-link'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Maklerempfehlung
    </span>{' '}
    beschrieben an Makler weitergeben.
  </p>
)

const userConsentNoteIntentBuy = (onLinkClick: () => void): React.JSX.Element => {
  return (
    <p data-testid='user-consent-note-buy'>
      Zur Erstellung einer auf dich zugeschnittenen Bewertung und Beratung rund um deinen Immobilienkauf geben wir deine
      Daten an unsere Kooperationspartner. Nähere Informationen findest du in unserem{' '}
      <span
        className='cursor-pointer'
        onClick={() => onLinkClick()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onLinkClick()
        }}
        role='button'
        tabIndex={0}
      >
        Hinweis Immobilienbewertung
      </span>
      .
    </p>
  )
}

const userConsentNoteIntentExtendedBuy = (onLinkClick: () => void): React.JSX.Element => {
  return (
    <p data-testid='user-consent-note-extended-buy'>
      Zur Erstellung personalisierter Angebote wird ImmoScout24 deine Daten wie im{' '}
      <span
        className='cursor-pointer'
        onClick={() => onLinkClick()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onLinkClick()
        }}
        role='button'
        tabIndex={0}
      >
        Hinweis Maklerempfehlung
      </span>{' '}
      beschrieben an Makler weitergeben.
    </p>
  )
}

const userConsentNoteIntentRentOut = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-rent-out'>
    Zur Erstellung einer auf dich zugeschnittenen Bewertung und Beratung rund um deinen Immobilienverkauf geben wir
    deine Daten an unsere Kooperationspartner. Nähere Informationen findest du in unserem{' '}
    <span
      className='cursor-pointer'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Maklerempfehlung
    </span>
    .
  </p>
)

const userConsentNoteIntentRentOutWithOrMaybeWithRealtor = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-rent-out-with-or-maybe-with-realtor'>
    Zur Erstellung einer auf dich zugeschnittenen Bewertung und Beratung rund um deinen Immobilienvermietung geben wir
    deine Daten an unsere Kooperationspartner. Nähere Informationen findest du in unserem{' '}
    <span
      className='cursor-pointer'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Maklerempfehlung
    </span>
    .
  </p>
)

const userConsentNoteIntentRentOutWithoutRealtor = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-rent-out-without-realtor'>
    Zur Erstellung einer auf dich zugeschnittenen Bewertung und Beratung rund um deine Immobilienvermietung geben wir
    deine Daten an unsere Kooperationspartner. Nähere Informationen findest du in unserem{' '}
    <span
      className='cursor-pointer'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Immobilienbewertung
    </span>
    .
  </p>
)

const userConsentNoteIntentRent = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-rent'>
    Zur Erstellung personalisierter Angebote wird ImmoScout24 deine Daten wie im{' '}
    <span
      className='cursor-pointer'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Immobilienbewertung
    </span>{' '}
    beschrieben nutzen.
  </p>
)

const userConsentNoteCommercialRealEstate = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-commercial'>
    Zur Erstellung personalisierter Angebote wird ImmoScout24 deine Daten wie im{' '}
    <span
      className='cursor-pointer'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Maklerempfehlung
    </span>{' '}
    beschrieben an Makler weitergeben
  </p>
)

const userConsentNoteIntentIndependentSaleAndConsultation = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-independent-sale-and-consultation'>
    Zur Erstellung personalisierter Angebote wird ImmoScout24 deine Daten wie im{' '}
    <span
      className='cursor-pointer'
      data-testid='user-consent-note-link'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Maklerempfehlung
    </span>{' '}
    beschrieben an Makler weitergeben.
  </p>
)

const userConsentNoteIntentIndependentSale = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-independent-sale'>
    Zur Erstellung einer auf dich zugeschnittenen Bewertung und Beratung rund um deinen Immobilienverkauf geben wir
    deine Daten an unsere Kooperationspartner. Nähere Informationen findest du in unserem{' '}
    <span
      className='cursor-pointer'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Immobilienbewertung.
    </span>{' '}
  </p>
)

const userConsentNoteIntentPartialSale = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-partial-sale'>
    Zur Erstellung eines personalisierten Angebots wird ImmoScout24 deine Daten wie im{' '}
    <span
      className='cursor-pointer'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Teilverkauf
    </span>{' '}
    beschrieben an Immobilienunternehmen weitergeben.
  </p>
)

const userConsentNoteIntentIndependentWithOfferSale = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-independent-with-offer-sale'>
    Zur Erstellung personalisierter Angebote wird ImmoScout24 deine Daten wie im{' '}
    <span
      className='cursor-pointer'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Käuferempfehlung
    </span>{' '}
    beschrieben an Kaufinteressenten weitergeben.
  </p>
)

const userConsentNoteForIV24Integration = (onLinkClick: () => void): React.JSX.Element => (
  <p data-testid='user-consent-note-iv24-integration'>
    Zur Erstellung einer auf Sie zugeschnittenen Bewertung leiten wir Ihre Daten an Immobilienexperten weiter. Für
    dieses Angebot kooperieren wir mit ImmoScout24. Weitere Informationen finden Sie in unserem{' '}
    <span
      className='cursor-pointer'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Bewertung
    </span>{' '}
  </p>
)

const userConsentNoteSolarIntent = (onLinkClick: () => void) => (
  <p data-testid='user-consent-note-solar-intent'>
    Zur Erstellung personalisierter Angebote wird ImmoScout24 Ihre Daten wie im{' '}
    <span
      className='cursor-pointer'
      onClick={() => onLinkClick()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onLinkClick()
      }}
      role='button'
      tabIndex={0}
    >
      Hinweis Solarempfehlung
    </span>{' '}
    beschrieben an Solarunternehmen oder die Wattfox GmbH weitergeben.
  </p>
)
